import { useTranslation } from "react-i18next";
import { Icon, Link, Text, Tooltip } from "@ttc3k/trekker";
import { Flex, Grid } from "styled-system/jsx";
import { StaticMap } from "../StaticMap";
import {
  createGoogleMapsUrl,
  formatPhoneNumber,
  getAddressString,
  getMultilingualString,
} from "shared/utils";
import { useMemo } from "react";
import { EventLocation, Maybe } from "gql/generated";
import { MultilingualLangKey } from "shared/types";
import { Dialpad, Mail, OpenNewWindow, WarningTriangle } from "iconoir-react";

type LocationCardProps = {
  location: Maybe<EventLocation>;
  index: number;
  showNonOperatorWarning: boolean;
  handleWindowOpen: (url: string, trackWithPostHog: boolean) => void;
  lang: MultilingualLangKey;
};

const LocationCard = ({
  location,
  index,
  showNonOperatorWarning,
  handleWindowOpen,
  lang,
}: LocationCardProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });

  const website = location?.contact?.links?.website ?? "";
  const phone = formatPhoneNumber(location?.contact?.phone ?? "");
  const email = location?.contact?.email ?? "";
  const googleMapsUrl = createGoogleMapsUrl(location?.contact?.address ?? {});

  return (
    <Grid
      key={`${location?.venue?.en}-${index}`}
      part="details__location-container"
      gridTemplateColumns="36px 1fr"
      gap="0"
      gridGap="0"
      gridColumnGap="0"
      alignItems="stretch"
      border="1px solid"
      borderColor="border.light"
      borderRadius="100"
    >
      <Flex
        part="details__location-container-left"
        flexDir="column"
        alignItems="center"
        justifyContent="center"
        gap="100"
        borderRight="1px solid"
        borderColor="border.light"
        borderTopLeftRadius="100"
        borderBottomLeftRadius="100"
        bgColor="var(--wa-colors-ink-100)"
      >
        {showNonOperatorWarning &&
          (!location?.locationInfo || !location?.locationInfo._entityId) && (
            <Tooltip
              content={t("WARNINGS.NON_OPERATOR_WARNING")}
              contentProps={{
                style: {
                  maxWidth: "300px",
                },
              }}
            >
              <Icon
                Element={WarningTriangle}
                w="20px"
                h="20px"
                color="icon.highlight.dark"
                flexShrink={0}
              />
            </Tooltip>
          )}
        <Text
          part="details__location-container-left-number"
          visual="smallCode"
          color="text.mid"
        >
          {index === -1 ? "-" : index + 1}
        </Text>
      </Flex>
      <Flex
        part="details__location-container-right"
        alignItems="center"
        justifyContent="center"
        gap="200"
        px="250"
        py="200"
        borderTopRightRadius="100"
        borderBottomRightRadius="100"
        bgColor="white"
      >
        <Flex
          part="details__location-container-right-content"
          flexDir="column"
          justifyContent="center"
          gap="50"
          w="full"
        >
          <Text
            part="details__location-name"
            visual="bodySemiBold"
            lineClamp={2}
            wordBreak="break-word"
          >
            {getMultilingualString(location?.venue ?? {}, lang)}
          </Text>
          <Link
            part="details__location-address-link"
            href={googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            color="text.light"
            textDecoration="underline"
            size="sm"
            w="fit-content"
          >
            <Text
              part="details__location-address-text"
              visual="smallMedium"
              color="text.mid"
              lineClamp={2}
              wordBreak="break-word"
            >
              {getAddressString(location?.contact?.address ?? {}, false, false)}
            </Text>
          </Link>
        </Flex>

        {(website || phone || email) && (
          <Flex
            part="details__location-container-right-icons"
            alignItems="center"
            gap="150"
            flexShrink={0}
          >
            {website && (
              <Icon
                part="details__icon-location-contact"
                Element={OpenNewWindow}
                onClick={() => handleWindowOpen(website, true)}
                tabIndex={0}
                w="20px"
                h="20px"
                cursor="pointer"
                color="icon.light"
                _hover={{
                  color: "icon.mid",
                }}
                _focusVisible={{
                  color: "icon.mid",
                }}
                outline="none"
              />
            )}

            {phone && (
              <Icon
                part="details__icon-location-contact"
                Element={Dialpad}
                onClick={() => handleWindowOpen(`tel:${phone}`, false)}
                tabIndex={0}
                w="20px"
                h="20px"
                cursor="pointer"
                color="icon.light"
                _hover={{
                  color: "icon.mid",
                }}
                _focusVisible={{
                  color: "icon.mid",
                }}
                outline="none"
              />
            )}

            {email && (
              <Icon
                part="details__icon-location-contact"
                Element={Mail}
                onClick={() => handleWindowOpen(`mailto:${email}`, false)}
                tabIndex={0}
                w="20px"
                h="20px"
                cursor="pointer"
                color="icon.light"
                _hover={{
                  color: "icon.mid",
                }}
                _focusVisible={{
                  color: "icon.mid",
                }}
                outline="none"
              />
            )}
          </Flex>
        )}
      </Flex>
    </Grid>
  );
};

type EventMapAndCardsProps = {
  eventLocations: Maybe<EventLocation>[];
  lang: MultilingualLangKey;
  showNonOperatorWarning?: boolean;
  handleWindowOpen: (url: string, trackWithPostHog: boolean) => void;
};

export const EventMapAndCards = ({
  eventLocations = [],
  lang,
  showNonOperatorWarning = false,
  handleWindowOpen,
}: EventMapAndCardsProps) => {
  const { t } = useTranslation("events", { keyPrefix: "WIDGET" });

  // Memoize the location coordinates to avoid re-rendering the map when the location changes
  const {
    locationCoordinates,
    locationsWithCoordinates,
    locationsWithoutCoordinates,
  } = useMemo(() => {
    const locationsWithCoordinates = eventLocations.filter(
      location =>
        location?.contact?.address?.location &&
        location?.contact?.address?.location?.coordinates &&
        location?.contact?.address?.location?.coordinates.length === 2 &&
        location?.contact?.address?.location?.coordinates[0] !== 0 &&
        location?.contact?.address?.location?.coordinates[1] !== 0,
    );
    const locationsWithoutCoordinates = eventLocations.filter(
      location =>
        // These places need at least a name
        location?.venue?.en &&
        (!location?.contact?.address?.location ||
          !location?.contact?.address?.location?.coordinates ||
          location?.contact?.address?.location?.coordinates.length !== 2 ||
          (location?.contact?.address?.location?.coordinates[0] === 0 &&
            location?.contact?.address?.location?.coordinates[1] === 0)),
    );

    const locationCoordinates =
      locationsWithCoordinates.map(location => ({
        latitude: location?.contact?.address?.location?.coordinates[1] ?? 0,
        longitude: location?.contact?.address?.location?.coordinates[0] ?? 0,
      })) ?? [];

    return {
      locationCoordinates,
      locationsWithCoordinates,
      locationsWithoutCoordinates,
    };
  }, [eventLocations]);

  return (
    <>
      {/* MAP */}
      {locationCoordinates.length > 0 && (
        <Flex part="details__map-container" flexDir="column" gap="250">
          <Text part="details__map-title" visual="smallLabel">
            {t("LABELS.LOCATION", { count: locationCoordinates.length })}
          </Text>
          <StaticMap
            style={{
              minHeight: "190px",
              transition: "height 0.3s ease",
            }}
            locations={locationCoordinates}
            alwaysShowPinLabel={true}
          />
        </Flex>
      )}

      {/* Event locations */}
      {(locationsWithCoordinates.length > 0 ||
        locationsWithoutCoordinates.length > 0) && (
        <Flex part="details__locations-container" flexDir="column" gap="200">
          {locationsWithCoordinates?.map((location, index) => (
            <LocationCard
              key={`${location?.venue?.en}-${index}`}
              location={location}
              index={index}
              showNonOperatorWarning={showNonOperatorWarning}
              handleWindowOpen={handleWindowOpen}
              lang={lang}
            />
          ))}
          {locationsWithoutCoordinates?.map((location, index) => (
            <LocationCard
              key={`${location?.venue?.en}-${index}`}
              location={location}
              index={-1}
              showNonOperatorWarning={showNonOperatorWarning}
              handleWindowOpen={handleWindowOpen}
              lang={lang}
            />
          ))}
        </Flex>
      )}
    </>
  );
};
